import { type Ref, reactive, watch } from 'vue';
import { throttle } from '@dh-io-owpi/shared/src/utils/fn.util';

/**
 * create a handler for a slider using a scrollable element
 */
export function useSlider(scrollableRef: Ref<HTMLElement | undefined>) {
  function slideTo(direction: -1 | 1) {
    const scrollable = scrollableRef.value;
    if (!scrollable) return;
    const width = scrollable.firstElementChild?.clientWidth ?? scrollable.clientWidth;
    scrollable.scrollBy({ left: direction * width, behavior: 'smooth' });
  }

  const state = reactive({
    atStart: true,
    atEnd: false,
    current: 0,
    slideTo,
  });

  function update() {
    const scrollable = scrollableRef.value;
    if (!scrollable) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollable;

    const atStart = scrollLeft === 0;
    const atEnd = scrollLeft + clientWidth >= scrollWidth;
    let current: number;

    if (atStart) current = 0;
    else if (atEnd) current = scrollable.children.length - 1;
    else {
      current = [].findIndex.call(
        scrollable.children, // HTMLCollection is not an array
        (child: HTMLElement) => child.getBoundingClientRect().left >= 0,
      );
    }

    Object.assign(state, { atStart, atEnd, current });
  }
  watch(scrollableRef, (scrollable) => {
    if (!scrollable) return;

    update();
    return observeScroll(scrollable, update);
  });

  return state;
}

/**
 * observe scroll events on a scrollable element
 * use 'scrollend' event if available, otherwise throttle the 'scroll' event
 */
function observeScroll(scrollable: HTMLElement, update: () => void) {
  let eventName = 'scrollend';
  if (!('scrollend' in window)) {
    eventName = 'scroll';
    update = throttle(update, 300);
  }

  scrollable.addEventListener(eventName, update, { passive: true });
  return () => scrollable.removeEventListener(eventName, update);
}
